import { useEffect, useMemo, useState } from 'react';
import { CompanyRequestError, companyRequest } from '@/company/api';
import { apiUrl, authHeaders } from '@/lib/shell';
import { z } from 'zod';
import {
  InspectBinding,
  InspectActionTicket,
} from '../../../src/apps/selection/host-contracts.mjs';
import { id } from '../../../src/artifacts/models/common.mjs';
import { savedArtifactDisplaySchema } from '../../../src/artifacts/saved-view-contract.mjs';
import { savedArtifactViewSchema } from '../../../src/artifacts/saved-view-contract.mjs';
import type { SavedArtifactView } from '../../../src/artifacts/saved-view-contract.mjs';
import type { ArtifactVersionHistory } from '../../../src/app-data/artifact-lifecycle/library.mjs';
import { artifactApi } from './api';
import { artifactCanvasScope, savedArtifactCanvasTarget } from './routes';
import type { ArtifactCanvasTarget, DraftCanvasTarget } from './routes';

export async function readSavedArtifactCanvas(
  target: ArtifactCanvasTarget,
): Promise<SavedArtifactView> {
  if (!savedArtifactCanvasTarget({ ...target, artifactId: target.id }))
    throw new CompanyRequestError(503, 'artifact_version_unavailable');
  const value = savedArtifactViewSchema.parse(
    await artifactApi.readVersion({ versionId: target.versionId, purpose: target.purpose }),
  );
  if (
    value.artifactId !== target.id ||
    value.versionId !== target.versionId ||
    value.revisionId !== target.revisionId ||
    value.number !== target.versionNumber ||
    value.purpose !== target.purpose
  )
    throw new CompanyRequestError(409, 'artifact_version_binding_changed');
  return value;
}

// What an open that the server refused says (DES1 share-copy-final), from the actual status and
// code the reader raised: a changed authorization asks the person to open it again; an open that is
// no longer allowed or no longer there is "no longer available to you", which names no cause
// (withdrawal, Unpublish or removal) and no owner (the saved view carries none). A service fault or
// a changed original target keeps the shell's own wording.
function openRefusal(error: CompanyRequestError): string {
  if (error.status === 409 && error.code === 'asset_authority_stale')
    return 'Your access changed. Open the Artifact again to continue.';
  if (
    error.status === 403 ||
    (error.status === 404 &&
      (error.code === 'version_not_found' || error.code === 'version_not_published'))
  )
    return 'This Artifact is no longer available to you.';
  return error.message;
}

export async function readSavedArtifactHistory(
  target: ArtifactCanvasTarget,
): Promise<ArtifactVersionHistory> {
  if (!savedArtifactCanvasTarget({ ...target, artifactId: target.id }))
    throw new CompanyRequestError(503, 'artifact_version_unavailable');
  const history = await artifactApi.readHistory({ artifactId: target.id, purpose: target.purpose });
  if (
    history.artifactId !== target.id ||
    history.purpose !== target.purpose ||
    history.load !== 'ready' ||
    !Array.isArray(history.versions) ||
    history.versions.length > 200 ||
    history.versions.some(
      (row) =>
        !savedArtifactCanvasTarget({
          artifactId: history.artifactId,
          versionId: row.versionId,
          revisionId: row.revisionId,
          versionNumber: row.number,
          purpose: history.purpose,
        }),
    ) ||
    new Set(history.versions.map((row) => row.versionId)).size !== history.versions.length
  )
    throw new CompanyRequestError(409, 'artifact_version_binding_changed');
  return history;
}

export function useSavedArtifactHistory(
  target: ArtifactCanvasTarget | DraftCanvasTarget,
  companyId: string,
  actorId: string,
  enabled: boolean,
) {
  const [attempt, setAttempt] = useState(0);
  const scopeKey = JSON.stringify([
    companyId,
    actorId,
    enabled,
    attempt,
    ...artifactCanvasScope(target),
  ]);
  const scope = useMemo(() => ({ key: scopeKey }), [scopeKey]);
  const [loaded, setLoaded] = useState<{
    scope: typeof scope;
    history: ArtifactVersionHistory | null;
  } | null>(null);
  useEffect(() => {
    if (!enabled || target.kind !== 'artifact' || !companyId || !actorId) return;
    let current = true;
    void readSavedArtifactHistory(target)
      .then((history) => {
        if (current) setLoaded({ scope, history });
      })
      .catch(() => {
        if (current) setLoaded({ scope, history: null });
      });
    return () => {
      current = false;
    };
  }, [scope]);
  return {
    history: loaded?.scope === scope ? loaded.history : null,
    load:
      loaded?.scope === scope
        ? loaded.history
          ? ('ready' as const)
          : ('error' as const)
        : ('loading' as const),
    retry: () => setAttempt((n) => n + 1),
  };
}

export type SavedArtifactCanvasState =
  | { status: 'loading' }
  | {
      status: 'ready';
      view: SavedArtifactView;
      /** Re-read the same saved view in place (after a sharing change), with no loading state. */
      refresh?: () => void;
    }
  | { status: 'error'; message: string; retry: () => void };
/** A change to ANY original version fact or presentation scope drops old pixels
 * immediately. Current server authority remains independent of URL hints. */
export function useSavedArtifactCanvas(
  target: ArtifactCanvasTarget | DraftCanvasTarget,
  companyId: string,
  actorId: string,
): SavedArtifactCanvasState {
  // Try again re-reads the same original tuple under current authority.
  const [attempt, setAttempt] = useState(0);
  const scopeKey = JSON.stringify([companyId, actorId, attempt, ...artifactCanvasScope(target)]);
  // Returning A -> B -> A creates a fresh read token even when B never answered.
  // Prior A pixels cannot reappear while current authority is being rechecked.
  const scope = useMemo(() => ({ key: scopeKey }), [scopeKey]);
  const [loaded, setLoaded] = useState<{
    scope: typeof scope;
    state: SavedArtifactCanvasState;
  } | null>(null);
  useEffect(() => {
    let current = true;
    const load = async () => {
      try {
        if (target.kind !== 'artifact' || !companyId || !actorId)
          throw new CompanyRequestError(503, 'artifact_service_unavailable');
        const view = await readSavedArtifactCanvas(target);
        // A refresh that fails keeps the view already shown.
        const refresh = () =>
          void readSavedArtifactCanvas(target).then(
            (next) =>
              current && setLoaded({ scope, state: { status: 'ready', view: next, refresh } }),
            () => undefined,
          );
        if (current) setLoaded({ scope, state: { status: 'ready', view, refresh } });
      } catch (error) {
        if (current)
          setLoaded({
            scope,
            state: {
              status: 'error',
              retry: () => setAttempt((n) => n + 1),
              message: openRefusal(
                error instanceof CompanyRequestError
                  ? error
                  : new CompanyRequestError(503, 'artifact_view_unavailable'),
              ),
            },
          });
      }
    };
    void load();
    return () => {
      current = false;
    };
    // The original scope includes every load input; changing only row/tab focus
    // keeps this authorized saved view and cannot reselect another version.
  }, [scope]);
  return loaded?.scope === scope ? loaded.state : { status: 'loading' };
}

// The existing root response composes canonical values. The number is the retained
// C9 source ordinal, separate from the current private display and preview binding.
const nativeEditorSchema = z.strictObject({
  draftId: id,
  binding: InspectBinding,
  display: savedArtifactDisplaySchema.transform((display, context) => {
    if (display.kind === 'app') {
      context.addIssue({ code: 'custom', message: 'Apps have no native editor' });
      return z.NEVER;
    }
    return display;
  }),
  ticket: InspectActionTicket.nullable(),
  number: z.number().int().positive(),
});
export type NativeEditorCanvas = z.infer<typeof nativeEditorSchema>;
export const nativeInspectFetch: typeof fetch = (input, init) => {
  const headers = new Headers(authHeaders());
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
  return fetch(apiUrl(String(input)), { ...init, headers });
};
export async function openNativeArtifactEditor(view: SavedArtifactView, operationId: string) {
  if (view.purpose !== 'editor') throw new CompanyRequestError(403, 'not_authorized');
  const result = nativeEditorSchema.parse(
    await companyRequest(
      `/api/v1/company/artifacts/${encodeURIComponent(view.artifactId)}/edit`,
      'POST',
      {
        artifactId: view.artifactId,
        versionId: view.versionId,
        revisionId: view.revisionId,
        number: view.number,
        purpose: 'editor',
        operationId,
      },
    ),
  );
  if (result.binding.artifactId !== view.artifactId || result.number !== view.number)
    throw new CompanyRequestError(409, 'artifact_version_binding_changed');
  return result;
}
export async function readNativeArtifactEditor(
  draftId: string,
  ticket?: z.infer<typeof InspectActionTicket>,
) {
  id.parse(draftId);
  const query = ticket
    ? `?ticket=${encodeURIComponent(JSON.stringify(InspectActionTicket.parse(ticket)))}`
    : '';
  const result = nativeEditorSchema.parse(
    await companyRequest(
      `/api/v1/company/artifacts/drafts/${encodeURIComponent(draftId)}/editor${query}`,
    ),
  );
  if (
    result.draftId !== draftId ||
    (result.ticket && result.ticket.artifactId !== result.binding.artifactId)
  )
    throw new CompanyRequestError(409, 'inspect_binding_mismatch');
  return result;
}
